import Divider from "@mui/material/Divider/Divider";
import Grid from "@mui/material/Grid";
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import { SelectChangeEvent } from "@mui/material/Select";
import { inviteEmployee, fetchEmployeesWithoutAccess } from '../../store/employeeStore/employeeThunk';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { fetchRoles } from "../../store/rolesThunk";

import Autocomplete from "@mui/material/Autocomplete/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography/Typography";
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Chip } from "@mui/material";
import { toast } from 'react-toastify';

// Form Input interface
interface IEmployeeInvite {
  employeeId: string;
  role: string;
  welcomeMessage?: string;
};

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
}

export default function EmployeeInviteDialog(val: IDialogProps) {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state => state.auth));
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);

  const [userRoles, setUserRoles] = useState<IOptions[]>([]);
  const [userRoleValues, setUserRoleValues] = useState("");
  const { status } = useAppSelector((state) => state.employees);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const schema = yup.object().shape({
    employeeId: yup.string().required('Employee is required.'),
    role: yup.string().required('Role is required.'),
    welcomeMessage: yup.string().optional(),
  });

  useEffect(() => {
    const getRoles = async () => {
      const data: any = await dispatch(fetchRoles());
      const roles = Array.isArray(data?.payload) ? data.payload : [];
      setUserRoles(roles.map((c: any) => {
        return { label: c.role.charAt(0).toUpperCase() + c.role.slice(1), value: c._id }
      }));
    }
    getRoles();
  }, [dispatch]);

  useEffect(() => {
    const getEmployeesWithoutAccess = async () => {
      if (val.open) {
        const data: any = await dispatch(fetchEmployeesWithoutAccess());
        if (data.payload && data.payload.success) {
          setEmployeeOptions(data.payload.data.map((emp: any) => ({
            ...emp,
            label: `${emp.name} (${emp.empId})`,
            searchText: `${emp.name} ${emp.empId} ${emp.email || ''} ${emp.designation || ''}`
          })));
        }
      }
    };
    getEmployeesWithoutAccess();
  }, [dispatch, val.open]);

  const methods = useForm<IEmployeeInvite>({
    resolver: yupResolver(schema),
    mode: "all",
    shouldUnregister: true
  });
  const { control } = methods;

  const onSubmit = async (data: IEmployeeInvite) => {
    try {
      const res = await dispatch(inviteEmployee({
        employeeId: data.employeeId,
        roleId: data.role,
        welcomeMessage: data.welcomeMessage || ''
      })).unwrap();
      toast.success(res?.message || "Invitation sent successfully");
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || "Failed to send invitation");
    }
  };

  const resetForm = () => {
    setUserRoleValues('');
    setSelectedEmployee(null);
    methods.reset({ 'employeeId': '', 'role': '', 'welcomeMessage': '' });
  };

  const handleClose = () => {
    resetForm();
    val.onClose();
  };

  const loading = false;

  const filterEmployees = (options: any[], { inputValue }: any) => {
    return options.filter(option => 
      option.searchText.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <div>
      <Dialog open={val.open} onClose={handleClose} fullWidth maxWidth="sm">
        <FormProvider {...methods}>
          <DialogTitle>
            <Box sx={{ display: "flex", mt: 2, justifyContent: "space-between" }}>
              <Typography variant="h5">Employee Invitation</Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Grid container columnSpacing={1} rowSpacing={2}>
              <Grid item md={12} xs={12}>
                <Controller
                  name='employeeId'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      id="employee"
                      value={selectedEmployee}
                      onChange={(event, newValue) => {
                        setSelectedEmployee(newValue);
                        field.onChange(newValue?._id || '');
                      }}
                      filterOptions={filterEmployees}
                      getOptionLabel={(option: any) => option.label || ''}
                      options={employeeOptions}
                      loading={loading}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option._id}>
                          <List sx={{ width: '100%', bgcolor: 'background.paper' }} dense>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar>
                                  {option.name.slice(0, 1)}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1">{option.name}</Typography>
                                    <Chip 
                                      label={option.empId} 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    {option.designation && (
                                      <Typography variant="body2" color="textSecondary">
                                        {option.designation}
                                      </Typography>
                                    )}
                                    {option.email && (
                                      <Typography variant="caption" color="textSecondary">
                                        {option.email}
                                      </Typography>
                                    )}
                                    {option.department && (
                                      <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                                        {option.department.name}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          </List>
                        </Box>
                      )}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Search Employee"
                          variant="standard"
                          required
                          helperText="Select an employee to send invitation"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            )
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item md={12} xs={12}>
                <CustomSelect
                  name="role"
                  label="Role*"
                  required
                  id="role"
                  variant="standard"
                  options={userRoles}
                  value={userRoleValues ?? ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    setUserRoleValues(event.target.value);
                    methods.setValue("role", event.target.value, {shouldValidate: true});
                  }}
                />
              </Grid>

              <Grid item md={12} xs={12}>
                <Controller
                  name='welcomeMessage'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Welcome Message (Optional)"
                      variant="standard"
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Add a personal welcome message for the employee..."
                    />
                  )}
                />
              </Grid>
            </Grid>

            {employeeOptions.length === 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary" align="center">
                  No employees available for invitation. All employees may already have user access.
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                px: 1,
                pb: 1
              }}
            >
              <Button
                variant='outlined'
                onClick={handleClose}
                sx={{ minWidth: 110, borderRadius: 2 }}
              >
                Cancel
              </Button>
              <LoadingButton
                onClick={methods.handleSubmit(onSubmit)}
                type="submit"
                loading={status === ApiStatus.LOADING}
                variant="contained"
                color="secondary"
                disabled={employeeOptions.length === 0}
                sx={{ minWidth: 140, borderRadius: 2 }}
              >
                Send Invite
              </LoadingButton>
            </Box>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </div>
  );
}
